import {
	Component,
	ElementRef,
	HostBinding,
	Input,
	OnDestroy,
	inject,
	input,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { ToolbarItem } from "common/toolbar/toolbar-item.interface";
import { UtilService } from "common/util.service";
import { ZoomToolbarService } from "common/zoom/zoom-toolbar/zoom-toolbar.service";
import { PreferencesService } from "core/preferences.service";
import { Subscription } from "rxjs";

import { ToolbarComponent } from "common/toolbar/toolbar.component";
import { ZoomToolbarComponent } from "common/zoom/zoom-toolbar/zoom-toolbar.component";
import { ButtonExpandCollapseComponent } from "common/buttons/button-expand-collapse.component";
import { MenuComponent } from "common/menu/menu.component";

const MINIMAL_COLLAPSIBLE_CARD_HEIGHT = "72px";

@Component({
	selector: "stages-card",
	templateUrl: "./card.component.html",
	providers: [ZoomToolbarService],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ToolbarComponent, ZoomToolbarComponent, ButtonExpandCollapseComponent, MenuComponent],
})
export class CardComponent implements OnDestroy {
	readonly zoomToolbarService = inject(ZoomToolbarService);
	readonly preferencesService = inject(PreferencesService);
	readonly utilService = inject(UtilService);
	readonly cardElementRef = inject(ElementRef);

	readonly toolbarId = input<string>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	toolbarItems: ToolbarItem[] = [];

	readonly menuId = input<string>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	menuItems: MenuItem[] = [];

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	menuNoCollapse?: boolean;

	// needed to to toggle child component and to detect if
	// a card menu function has to be force triggered
	readonly externalToggleFunctions = input<MenuItem[]>([]);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	contentCollapsible?: boolean;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	showCollapseOption?: boolean;

	readonly showHeader = input<boolean>(true);

	readonly tabsContainer = viewChild<ElementRef>("tabsContainer");

	// To collapse the card till the headline,
	// you have to change the min-height attribute
	@HostBinding("style.min-height")
	minHeight = "";

	@HostBinding("style.transition")
	transition = "";

	expanded: boolean = true;
	title?: string;
	showZoomToolbar: boolean = false;
	private subscription?: Subscription;
	isIE: boolean = false;

	constructor() {
		const utilService = this.utilService;

		if (this.zoomToolbarService.isBrowserSupported) {
			this.subscription = this.zoomToolbarService.amountOfVisibleDiagrams.subscribe({
				next: (value) => (this.showZoomToolbar = value > 0),
			});
			this.zoomToolbarService.applyZoomParametersFromLocalStorage();
		}
		this.isIE = utilService.browserIsIE();
	}

	resetHeight(): void {
		this.minHeight = this.contentCollapsible ? MINIMAL_COLLAPSIBLE_CARD_HEIGHT : "";
	}

	toggleContentView(): void {
		if (this.externalToggleFunctions().length > 0) {
			this.expanded = !this.expanded;
			this.externalToggleFunctions().forEach((externalToggle) => externalToggle.on());
			this.resetHeight();
		}
	}

	// Expands the content that is collapsed by toggle function
	forceShowContent(): void {
		if (!this.expanded) {
			this.toggleContentView();
		}
	}

	getHeight(): number {
		return this.cardElementRef.nativeElement.offsetHeight;
	}

	setHeight(newHeight: string): void {
		this.minHeight = newHeight;
	}

	activateTransition(): void {
		this.transition = "min-height 0.3s 0s cubic-bezier(0.4, 0, 0.2, 1)";
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}
